import { Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { KENDO_BUTTON } from '@progress/kendo-angular-buttons';
import { KENDO_SVGICON } from '@progress/kendo-angular-icons';
import { LoaderComponent } from '@progress/kendo-angular-indicators';
import { KENDO_CARD, KENDO_EXPANSIONPANEL, KENDO_GRIDLAYOUT, KENDO_STACKLAYOUT } from '@progress/kendo-angular-layout';
import { UnityWorkflowState } from '@unity/clients';
import { FormFillModeDirective } from '@unity/directives/form-fill-mode.directive';
import { TrackFormDirective } from '@unity/directives/track-form.directive';
import { DetailFormService } from '@unity/services/detail-form.service';
import { FeatureService } from '@unity/services/feature.service';
import { IconService } from '@unity/services/icon.service';
import { LayoutService } from '@unity/services/layout.service';
import { ChipAuditPopupSharedComponent } from '@unity/shared/chip-audit-popup-shared/chip-audit-popup-shared.component';
import { ChipWorkflowStateSharedComponent } from '@unity/shared/presentation/chip-workflow-state-shared/chip-workflow-state-shared.component';
import { DetailedDto, FormEditState, UnityFeatureTypeStrings } from '@unity/utils/types';

const EditingState: FormEditState = {
  editing: true,
  fillMode: 'outline',
};

const LockedState: FormEditState = {
  editing: false,
  fillMode: 'flat',
};

@Component({
  selector: 'unity-panel-manage-shared',
  standalone: true,
  imports: [
    KENDO_CARD,
    KENDO_BUTTON,
    KENDO_GRIDLAYOUT,
    KENDO_EXPANSIONPANEL,
    KENDO_STACKLAYOUT,
    KENDO_SVGICON,
    ReactiveFormsModule,
    FormFillModeDirective,
    TrackFormDirective,
    ChipWorkflowStateSharedComponent,
    LoaderComponent,
    ChipAuditPopupSharedComponent,
  ],
  templateUrl: './panel-manage-shared.component.html',
  styleUrl: './panel-manage-shared.component.scss',
})
export class PanelManageSharedComponent {
  private readonly iconService = inject(IconService);
  private readonly layoutService = inject(LayoutService);
  private readonly featureService = inject(FeatureService);
  private readonly detailFormService = inject(DetailFormService);

  protected detailCardGridLayoutGap = this.layoutService.detailCardGridLayoutGap;

  protected saveIcon = this.iconService.saveIcon;
  protected editIcon = this.iconService.pencilIcon;
  protected cancelIcon = this.iconService.cancelIcon;

  protected readonly $editState = signal<FormEditState>(LockedState);

  public readonly $formGroup = input.required<FormGroup>({ alias: 'formGroup' });
  public readonly $isEditable = input.required<boolean | undefined>({ alias: 'isEditable' });
  public readonly $submitLoading = input.required<boolean | undefined>({ alias: 'submitLoading' });
  public readonly $resource = input.required<DetailedDto | undefined>({ alias: 'resource' });
  public readonly $resourceName = input.required<string>({ alias: 'resourceName' });

  public readonly $feature = input<UnityFeatureTypeStrings>(undefined, { alias: 'feature' });
  public readonly $headerMeta = input<string>('Details', { alias: 'headerMeta' });
  public readonly $workflowCanChange = input(true, { alias: 'workflowCanChange' });

  public saveEvent = output<void>();
  public detailEditEvent = output<FormEditState>();

  protected readonly $currentStatus = computed(() => {
    const resource = this.$resource();
    return resource ? resource.current_state : { id: 0, enum_value: UnityWorkflowState.Unknown, name: 'Unknown' };
  });

  protected readonly $canEdit = computed(() => {
    const isEditable = this.$isEditable();
    const currentStatus = this.$currentStatus();
    const feature = this.$feature();
    const featureEnabled = this.featureService.isEnabled(feature);

    return isEditable === true && currentStatus?.enum_value !== UnityWorkflowState.Disabled && featureEnabled === true;
  });

  public constructor() {
    effect(() => {
      const submitLoading = this.$submitLoading();
      if (submitLoading === undefined) {
        return;
      }
      const formGroup = untracked(this.$formGroup);
      if (submitLoading === false && !this.hasErrors(formGroup)) {
        this.$editState.set(LockedState);
        this.detailEditEvent.emit(LockedState);
      }
    });
  }

  private hasErrors(formGroup: FormGroup) {
    return formGroup.errors || Object.values(formGroup.controls).some(x => x.errors);
  }

  private resetAndDisableForm() {
    const formGroup = this.$formGroup();
    formGroup.reset();
    formGroup.disable();
  }

  protected setEditState(on: boolean) {
    // Edit if locked
    if (on) {
      this.$formGroup().enable();
      this.$editState.set(EditingState);
      this.detailEditEvent.emit(EditingState);
    }
    // re-lock if editing
    if (!on) {
      this.doNoOp();
    }
  }

  private doNoOp() {
    this.resetAndDisableForm();
    this.$editState.set(LockedState);
    this.detailEditEvent.emit(LockedState);
  }

  private doSave() {
    this.saveEvent.emit();
    this.$formGroup().disable();
  }

  protected onSave() {
    const formGroup = this.$formGroup();
    formGroup.updateValueAndValidity();
    if (formGroup.pristine) {
      this.doNoOp();
    } else if (formGroup.valid) {
      this.doSave();
    } else {
      this.detailFormService.handleFormErrors(formGroup);
    }
  }
}
